/**
 * A4 printable training-course register.
 *
 * Opens in its own tab from the course detail page. Fires `window.print()`
 * automatically once the data loads so staff hit "Print register" and
 * land in the print dialog with zero extra clicks.
 *
 * The layout is designed for the door-person on the day:
 *   - Course name + date banner at the top
 *   - One row per trainee with a **big empty checkbox** on the left
 *     for manual ticking if wifi drops out
 *   - Trainee name in large bold, contact in smaller grey
 *   - Guest rows indented under their primary booker with a "🎟 Guest of X" tag
 *   - Balance-due chip on the right (bold £ owing, so the door person can
 *     confirm cash before letting anyone in)
 *   - Signature line under every row for physical audit trail
 *   - Compact table: ~30 rows per A4 page (auto-densifies above 26 rows) so a course fits one sheet
 *
 * All app chrome (header, nav) is hidden via `@media print` so the printed
 * page is just the register itself.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import API_URL from '@/config/api';

// Reusable balance resolver — mirrors the logic in TrainingCourseDetail.
// Keeps the two views in sync when structured-vs-imported balance data
// is inconsistent. Legacy imports may have `balance_owed=0` even though
// the trainee actually owes — in that case we prefer col I (Final
// Balance = CURRENT) over col G (Balance on booking = HISTORICAL).
function resolveBalance(r) {
  if (r.balance_paid_at) return { amount: 0, paid: true, hasData: true };
  const structured = Number(r.balance_owed || 0);
  if (structured > 0) return { amount: structured, paid: false, hasData: true };
  const bd = r.booking_details || {};
  const finalRaw = String(bd.final_balance ?? '').trim();
  const bookingRaw = String(bd.balance_on_booking ?? '').trim();
  const importedRaw = finalRaw !== '' ? finalRaw : bookingRaw;
  if (importedRaw !== '') {
    const parsed = parseFloat(importedRaw.replace(/[£,\s]/g, ''));
    if (Number.isFinite(parsed)) {
      return { amount: Math.max(0, parsed), paid: parsed <= 0, hasData: true };
    }
  }
  if ((r.payment_received || 0) > 0 || (r.payment_total || 0) > 0) {
    return { amount: 0, paid: true, hasData: true };
  }
  return { amount: 0, paid: false, hasData: false };
}

function formatDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
    });
  } catch (_e) {
    return iso;
  }
}

export default function TrainingCoursePrintRegister() {
  const { id: courseId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/api/staff/training-courses/${courseId}`, {
          credentials: 'include',
        });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const d = await r.json();
        if (!alive) return;
        setData(d);
      } catch (e) {
        if (alive) setError(String(e.message || e));
      }
    })();
    return () => { alive = false; };
  }, [courseId]);

  // Auto-fire the print dialog once the DOM has painted. Slight delay so
  // web fonts / balance chips settle before Chrome snapshots the page.
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => { window.print(); }, 500);
    return () => clearTimeout(t);
  }, [data]);

  const totalOwed = useMemo(() => {
    if (!data) return 0;
    return (data.registrations || []).reduce((sum, r) => {
      const b = resolveBalance(r);
      return b.paid ? sum : sum + b.amount;
    }, 0);
  }, [data]);

  // Group the register so guests display under their primary booker
  // in the printed output, matching what the staff eyeball on-screen.
  const orderedRows = useMemo(() => {
    if (!data) return [];
    const rows = data.registrations || [];
    const byPrimary = new Map();
    const primaries = [];
    for (const r of rows) {
      if (!r.is_guest) {
        primaries.push(r);
        byPrimary.set(r.id, []);
      }
    }
    for (const r of rows) {
      if (r.is_guest && r.guest_of_registration_id && byPrimary.has(r.guest_of_registration_id)) {
        byPrimary.get(r.guest_of_registration_id).push(r);
      }
    }
    // Any orphan guests (no linked primary) fall to the bottom under a
    // synthetic bucket — better than losing them off the printed page.
    const orphans = rows.filter(
      (r) => r.is_guest && (!r.guest_of_registration_id || !byPrimary.has(r.guest_of_registration_id)),
    );
    const flat = [];
    for (const p of primaries) {
      flat.push(p);
      for (const g of byPrimary.get(p.id) || []) flat.push(g);
    }
    for (const o of orphans) flat.push(o);
    return flat;
  }, [data]);

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-red-700 font-semibold">Failed to load register: {error}</p>
        <button className="mt-4 underline" onClick={() => navigate(-1)}>Go back</button>
      </div>
    );
  }
  if (!data) {
    return <div className="p-8 text-center text-gray-500">Loading register…</div>;
  }

  const course = data.course || {};

  return (
    <>
      {/* Print CSS — kept inline so the page is self-contained and staff
          can open the URL, hit Cmd+P and land on an A4 layout without any
          extra setup. `@page { size: A4 portrait; margin: 12mm; }` sets
          the sheet size; the `.print-hide` class hides the top toolbar. */}
      <style>{`
        @page { size: A4 portrait; margin: 10mm; }
        @media print {
          body { background: #fff !important; }
          .print-hide { display: none !important; }
          tr { break-inside: avoid; page-break-inside: avoid; }
          thead { display: table-header-group; }
        }
        .print-register-page { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; color: #111; max-width: 800px; margin: 0 auto; padding: 16px 16px 24px; }
        .reg { width: 100%; border-collapse: collapse; table-layout: fixed; }
        .reg th { text-align: left; font-size: 9px; text-transform: uppercase; letter-spacing: .08em; color: #555; border-bottom: 2px solid #111; padding: 2px 4px; }
        .reg td { border-bottom: 1px solid #ddd; padding: 3px 4px; vertical-align: middle; line-height: 1.15; }
        .reg.dense td { padding: 1px 4px; font-size: 11px; }
        .reg.dense .name { font-size: 11.5px; }
        .box { width: 14px; height: 14px; border: 1.5px solid #111; border-radius: 2px; display: inline-block; vertical-align: middle; }
        .name { font-weight: 700; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sub { font-size: 10px; color: #666; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
        .sig { border-bottom: 1px dotted #999; height: 14px; }
        .due { font-weight: 700; color: #be123c; white-space: nowrap; }
        .paid { font-weight: 700; color: #047857; font-size: 11px; }
        .tag { font-size: 8px; font-weight: 700; text-transform: uppercase; background: #fef3c7; color: #92400e; border-radius: 999px; padding: 0 5px; margin-left: 4px; vertical-align: middle; }
      `}</style>

      <div className="print-register-page bg-white min-h-screen">
        <div className="print-hide mb-4 flex items-center justify-between gap-2 border-b border-gray-200 pb-3">
          <p className="text-xs text-gray-500">Print dialog opens automatically. If it didn&apos;t, tap the button on the right.</p>
          <button type="button" onClick={() => window.print()} className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500" data-testid="print-register-print">Print</button>
        </div>

        {/* Compact banner — one line of title, one line of stats */}
        <div className="mb-2 flex items-end justify-between gap-3 flex-wrap border-b-2 border-black pb-1.5">
          <div className="min-w-0">
            <h1 className="text-lg font-bold leading-tight truncate" data-testid="print-register-title">{course.name}</h1>
            <p className="text-xs text-gray-700">{formatDate(course.date)}{course.reference ? <span className="font-mono uppercase text-gray-500"> · {course.reference}</span> : null}</p>
          </div>
          <div className="flex gap-4 text-xs">
            <div><span className="text-gray-500 uppercase tracking-wider text-[9px] font-bold">Attendees</span> <b className="text-sm" data-testid="print-attendees-count">{orderedRows.length}</b></div>
            <div><span className="text-gray-500 uppercase tracking-wider text-[9px] font-bold">To collect</span> <b className="text-sm" data-testid="print-balance-total">£{totalOwed.toFixed(2)}</b></div>
            <div><span className="text-gray-500 uppercase tracking-wider text-[9px] font-bold">Grants</span> <b className="text-sm">{(course.sections || []).map((x) => x.charAt(0).toUpperCase() + x.slice(1)).join(', ') || '—'}</b></div>
          </div>
        </div>

        {orderedRows.length === 0 ? (
          <p className="mt-6 text-gray-500 italic">No trainees on the register.</p>
        ) : (
          <table className={`reg ${orderedRows.length > 26 ? 'dense' : ''}`} data-testid="print-register-table">
            <colgroup><col style={{ width: 24 }} /><col style={{ width: '34%' }} /><col /><col style={{ width: 78 }} /><col style={{ width: 110 }} /></colgroup>
            <thead><tr><th aria-label="Here"></th><th>Trainee</th><th>Contact / note</th><th style={{ textAlign: 'right' }}>Due</th><th>Signature</th></tr></thead>
            <tbody>
              {orderedRows.map((r) => {
                const bal = resolveBalance(r);
                const contact = [!r.email_synthetic ? r.email : null, r.phone].filter(Boolean).join(' · ');
                const note = r.booking_details && r.booking_details.notes;
                return (
                  <tr key={r.id} data-testid={`print-row-${r.id}`}>
                    <td><span className="box" aria-hidden="true" /></td>
                    <td style={r.is_guest ? { paddingLeft: 14 } : undefined}>
                      <div className="name">{r.name}{r.is_guest && <span className="tag">+1</span>}</div>
                      {r.guest_actual_name && <div className="sub" style={{ color: '#92400e' }}>Attendee: <b>{r.guest_actual_name}</b></div>}
                    </td>
                    <td>
                      {contact && <div className="sub" style={{ color: '#333' }}>{contact}</div>}
                      {note && <div className="sub" style={{ fontStyle: 'italic' }} title={note}>{note}</div>}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {bal.hasData ? (bal.paid ? <span className="paid">PAID</span> : <span className="due">£{bal.amount.toFixed(2)}</span>) : <span className="sub">—</span>}
                    </td>
                    <td><div className="sig" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <div className="mt-4 pt-2 border-t border-gray-300 flex flex-wrap justify-between text-[10px] text-gray-600 gap-4">
          <div className="flex items-end gap-2"><span className="uppercase tracking-wider font-bold text-gray-500">Door person</span><div className="border-b border-gray-400 w-40 h-4" /></div>
          <div className="flex items-end gap-2"><span className="uppercase tracking-wider font-bold text-gray-500">Signature</span><div className="border-b border-gray-400 w-40 h-4" /></div>
          <div><span className="uppercase tracking-wider font-bold text-gray-500">Printed</span> <span className="font-mono">{new Date().toLocaleString('en-GB')}</span></div>
        </div>
      </div>
    </>
  );
}
